import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

import logo from "@/assets/logo.png";

type AuthCardProps = {
  children: ReactNode;
  className?: string;
  /** Wider shell for multi-step flows */
  wide?: boolean;
};

export function AuthCard({ children, className, wide = false }: AuthCardProps) {
  return (
    <div
      className={cn(
        "w-full rounded-[28px] border border-[#E7E4DE] bg-white px-6 py-8 text-[#292d32] sm:px-10 sm:py-10",
        wide ? "max-w-[560px]" : "max-w-[440px]",
        className,
      )}
    >
      {children}
    </div>
  );
}

type AuthPageHeaderProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  className?: string;
  align?: "left" | "center";
  showLogo?: boolean;
};

export function AuthPageHeader({
  eyebrow,
  title,
  description,
  className,
  align = "left",
  showLogo = true,
}: AuthPageHeaderProps) {
  const centered = align === "center";

  return (
    <div className={cn("mb-8 sm:mb-10", centered && "text-center", className)}>
      {showLogo ? (
        <Link
          to="/"
          aria-label="Sqaleshop home"
          className={cn(
            "inline-flex items-center transition-opacity hover:opacity-80",
            centered && "mx-auto",
          )}
        >
          <img src={logo} alt="Sqaleshop" className="h-9 w-auto" />
        </Link>
      ) : null}

      {eyebrow ? (
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-[0.14em] text-[#6B6B66]",
            showLogo ? "mt-6" : "mt-0",
          )}
        >
          {eyebrow}
        </p>
      ) : null}

      <h1
        className={cn(
          "font-heading text-[1.65rem] font-bold leading-tight tracking-tight text-[#1a1523]",
          eyebrow || showLogo ? "mt-3" : "mt-0",
          centered && "mx-auto",
        )}
      >
        {title}
      </h1>
      {description ? (
        <p
          className={cn(
            "mt-1.5 max-w-md text-sm font-medium leading-relaxed text-[#6B6B66]",
            centered && "mx-auto",
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

/** Shared field / CTA classes for auth forms */
export const authFieldClass =
  "h-12 rounded-xl border-[#E4E0D8] bg-white px-4 text-[#292d32] shadow-none placeholder:text-[#9A9A96] focus-visible:border-primary/40";

export const authButtonClass =
  "h-12 w-full rounded-xl text-[15px] font-semibold shadow-none";

export const authOutlineButtonClass =
  "h-12 w-full rounded-xl border-[#E4E0D8] font-semibold shadow-none";

export const authNoticeClass =
  "mb-4 flex items-center justify-center gap-2 rounded-xl bg-[#f6f4ef] px-4 py-3.5 text-sm font-medium text-[#3b1578]"

const legalLinkClass =
  "font-semibold text-primary underline-offset-2 hover:underline"

type AuthLegalNoticeProps = {
  action?: "continue" | "create"
  className?: string
}

export function AuthLegalNotice({
  action = "continue",
  className,
}: AuthLegalNoticeProps) {
  const lead =
    action === "create"
      ? "By creating an account, you agree to our"
      : "By continuing, you agree to our"

  return (
    <p
      className={cn(
        "mt-8 text-center text-xs font-medium leading-relaxed text-muted-foreground sm:text-[13px]",
        className,
      )}
    >
      {lead}{" "}
      <Link to="/terms" className={legalLinkClass}>
        Terms &amp; Conditions
      </Link>
      ,{" "}
      <Link to="/privacy" className={legalLinkClass}>
        Privacy policy
      </Link>
      , and{" "}
      <Link to="/merchant-agreement" className={legalLinkClass}>
        Merchant agreement
      </Link>
      .
    </p>
  )
}
